import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import ImageExt from "@tiptap/extension-image";
import LinkExt from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import ConfirmModal from "./ConfirmModal";
import PromptModal from "./PromptModal";
import ColorMenu from "./ColorMenu";
import { extractFiles, buildAttachmentLinkHtml } from "./upload-helpers";
import { useAutosave } from "./useAutosave";
import {
    X,
    Plus,
    MoreVertical,
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Strikethrough,
    Baseline,
    Highlighter,
    Heading1,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    Quote,
    Code,
    Minus,
    Link,
    Image,
    ImagePlus,
    Undo,
    Redo,
} from "lucide-react";

// Files dropped or pasted into the editor are uploaded to blob storage via the
// page attachments endpoint. Keep these limits in sync with AttachmentsService.cs.
// Images embed inline; other file types insert as a download link.
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
const ALLOWED_INLINE_IMAGE_TYPES = [
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/webp",
    "image/svg+xml",
];

// Text colours are picked for contrast on white; highlights are pale enough to
// keep the default body colour readable on top of them.
const TEXT_COLORS = [
    { label: "Slate", value: "#475569" },
    { label: "Red", value: "#dc2626" },
    { label: "Orange", value: "#ea580c" },
    { label: "Amber", value: "#b45309" },
    { label: "Green", value: "#16a34a" },
    { label: "Teal", value: "#0d9488" },
    { label: "Blue", value: "#2563eb" },
    { label: "Indigo", value: "#4f46e5" },
    { label: "Purple", value: "#9333ea" },
    { label: "Pink", value: "#db2777" },
];

const HIGHLIGHT_COLORS = [
    { label: "Yellow", value: "#fef08a" },
    { label: "Green", value: "#bbf7d0" },
    { label: "Blue", value: "#bfdbfe" },
    { label: "Purple", value: "#e9d5ff" },
    { label: "Pink", value: "#fbcfe8" },
    { label: "Orange", value: "#fed7aa" },
    { label: "Red", value: "#fecaca" },
    { label: "Grey", value: "#e2e8f0" },
];

interface PageData {
    id: string;
    categoryId: string;
    folderId: string | null;
    title: string;
    body: string | null;
    status: string;
    // pending unpublished edits held for an already-published page
    draftBody?: string | null;
    draftTitle?: string | null;
    hasDraft?: boolean;
}

interface Category {
    id: string;
    name: string;
}

interface Folder {
    id: string;
    name: string;
}

interface Props {
    page: PageData | null;
    categoryId: string;
    folderId: string | null;
    categories: Category[];
    reviewProposalId?: string;
    onSave: (pageId: string, categoryId: string) => void;
    onClose: () => void;
}

export default function PageEditor({ page, categoryId, folderId, categories, reviewProposalId, onSave, onClose }: Props) {
    const isEdit = !!page;
    // Resume a pending draft when editing an already-published page (but not while
    // reviewing a suggested edit — that flow seeds the body with the proposal).
    const usingDraft = !reviewProposalId && page?.status === "published" && !!page?.draftBody;
    const [title, setTitle] = useState((usingDraft ? page?.draftTitle : page?.title) || page?.title || "");
    const [saving, setSaving] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [saveStatus, setSaveStatus] = useState<"" | "saving" | "saved" | "error">("");
    const [dirty, setDirty] = useState(false);
    const [folder, setFolder] = useState<Folder | null>(null);
    const [promptModal, setPromptModal] = useState<{ title: string; placeholder: string; onConfirm: (v: string) => void } | null>(null);
    const [closeConfirm, setCloseConfirm] = useState(false);
    const [savedCloseConfirm, setSavedCloseConfirm] = useState(false);
    const [discardConfirm, setDiscardConfirm] = useState(false);
    // Once a draft exists it keeps tracking the editor even if the text is reverted,
    // so the draft can't be left holding an edit the author has taken back.
    const [draftPersisted, setDraftPersisted] = useState(false);
    // Until then a published page waits for a real change, so clicking into a live
    // page — or typing something and removing it — leaves it alone.
    const [contentDiffers, setContentDiffers] = useState(false);
    const baselineRef = useRef<{ title: string; body: string } | null>(null);
    const isPublished = page?.status === "published";
    const titleRef = useRef<HTMLInputElement>(null);
    const pageIdRef = useRef<string | null>(page?.id || null);
    const [pageIdState, setPageIdState] = useState<string | null>(page?.id || null);
    // Read after awaits, so a ref rather than state.
    const persistedRef = useRef(false);
    // Autosave can outlive the editor: a flush fired on unmount must not set state.
    const mountedRef = useRef(true);
    const createLockRef = useRef<Promise<string | null> | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const autosaveScheduleRef = useRef<(() => void) | null>(null);
    // Refs let the editor's paste/drop handlers — created once, on mount —
    // reach the latest upload functions and editor instance without going stale.
    const editorRef = useRef<ReturnType<typeof useEditor> | null>(null);
    const uploadImageRef = useRef<((file: File) => Promise<string | null>) | null>(null);
    const uploadAttachmentRef = useRef<((file: File) => Promise<{ url: string; filename: string } | null>) | null>(null);

    // Route each dropped/pasted file by type: images embed inline, everything
    // else uploads as an attachment and inserts a download link.
    const insertUploadedFiles = (files: File[]) => {
        files.forEach(file => {
            if (file.type.startsWith("image/")) {
                uploadImageRef.current?.(file).then(src => {
                    if (src) editorRef.current?.chain().focus().setImage({ src }).run();
                });
            } else {
                uploadAttachmentRef.current?.(file).then(res => {
                    if (res) editorRef.current?.chain().focus()
                        .insertContent(buildAttachmentLinkHtml(res.url, res.filename)).run();
                });
            }
        });
    };

    const category = categories.find(c => c.id === categoryId);

    const editor = useEditor({
        editorProps: {
            handlePaste: (_view, event) => {
                const files = extractFiles(event.clipboardData);
                if (files.length === 0) return false;
                event.preventDefault();
                insertUploadedFiles(files);
                return true;
            },
            handleDrop: (_view, event) => {
                const files = extractFiles((event as DragEvent).dataTransfer);
                if (files.length === 0) return false;
                event.preventDefault();
                insertUploadedFiles(files);
                return true;
            },
        },
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
            }),
            Placeholder.configure({
                placeholder: "Type your text...",
            }),
            ImageExt.configure({
                inline: false,
                allowBase64: true,
            }),
            LinkExt.configure({
                openOnClick: false,
                autolink: true,
            }),
            Underline,
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
        ],
        content: (usingDraft ? page?.draftBody : page?.body) || "",
        onUpdate: () => {
            setDirty(true);
            evaluateDiff();
            autosaveScheduleRef.current?.();
        },
    });
    editorRef.current = editor;

    const setPageId = (id: string | null) => {
        pageIdRef.current = id;
        if (mountedRef.current) setPageIdState(id);
    };

    // Compare against the editor's own serialisation, not the stored body: tiptap
    // normalises the HTML it is given, so those differ even on an untouched load.
    function evaluateDiff() {
        const baseline = baselineRef.current;
        const ed = editorRef.current;
        if (!baseline || !ed) return;
        const currentTitle = (titleRef.current?.value ?? "").trim();
        setContentDiffers(ed.getHTML() !== baseline.body || currentTitle !== baseline.title);
    }

    useEffect(() => {
        if (editor && !baselineRef.current) {
            baselineRef.current = { title: (titleRef.current?.value ?? title).trim(), body: editor.getHTML() };
        }
    }, [editor, title]);

    useEffect(() => () => { mountedRef.current = false; }, []);

    // fetch folder name for breadcrumb
    useEffect(() => {
        const fId = folderId || page?.folderId;
        if (fId) {
            axios.get(`/api/manuals/folders/${fId}`)
                .then(res => { if (res.data.success) setFolder(res.data.item); })
                .catch(() => {});
        }
    }, [folderId, page?.folderId]);

    // focus title on mount for new pages
    useEffect(() => {
        if (!isEdit && titleRef.current) {
            titleRef.current.focus();
        }
    }, []);

    // claim edit lock on mount, release on unmount
    useEffect(() => {
        if (page?.id) {
            axios.post(`/api/manuals/pages/${page.id}/edit-lock`).catch(() => {});
        }
        return () => {
            const id = pageIdRef.current;
            if (id) {
                axios.delete(`/api/manuals/pages/${id}/edit-lock`).catch(() => {});
            }
        };
    }, [page?.id]);

    // heartbeat every 2 min
    useEffect(() => {
        if (!page?.id) return;
        const interval = setInterval(() => {
            axios.post(`/api/manuals/pages/${page.id}/edit-lock/heartbeat`).catch(() => {});
        }, 120000);
        return () => clearInterval(interval);
    }, [page?.id]);

    const ensureCreated = (currentTitle: string, body: string): Promise<string | null> => {
        if (pageIdRef.current) return Promise.resolve(pageIdRef.current);
        if (createLockRef.current) return createLockRef.current;
        const p = axios.post("/api/manuals/pages", {
            categoryId,
            folderId: folderId || null,
            title: currentTitle.trim(),
            body,
        })
            .then(res => {
                if (res.data.success) {
                    setPageId(res.data.item.id);
                    // The unmount cleanup has already run with no id, so a create that
                    // lands after the editor closed would leave the lock held forever.
                    if (mountedRef.current) {
                        axios.post(`/api/manuals/pages/${res.data.item.id}/edit-lock`).catch(() => {});
                    }
                    return res.data.item.id as string;
                }
                return null;
            })
            .catch(() => null)
            .finally(() => { createLockRef.current = null; });
        createLockRef.current = p;
        return p;
    };

    // Persists the working copy without publishing: for a published page the backend
    // stores it as a pending draft (live page unchanged); for a new/unpublished page
    // it writes the draft body directly. Returns the page id.
    const persistDraft = async (currentTitle: string, body: string): Promise<string | null> => {
        const status = (s: "" | "saving" | "saved" | "error") => { if (mountedRef.current) setSaveStatus(s); };
        status("saving");
        try {
            if (pageIdRef.current) {
                const res = await axios.put(`/api/manuals/pages/${pageIdRef.current}`, {
                    folderId: (page?.folderId ?? folderId) || null,
                    title: currentTitle.trim(),
                    body,
                });
                if (!res.data.success) { status("error"); return null; }
            } else if (!(await ensureCreated(currentTitle, body))) {
                status("error");
                return null;
            }
            status("saved");
            if (mountedRef.current) {
                setDirty(false);
                setDraftPersisted(true);
            }
            persistedRef.current = true;
            return pageIdRef.current;
        } catch {
            status("error");
            return null;
        }
    };

    // Autosave runs silently: a blank title skips the cycle rather than nagging, and
    // a failure only shows in the status chip — the next edit reschedules a retry.
    const autosaveNow = async () => {
        if (!editor) return;
        const currentTitle = titleRef.current?.value || title;
        if (!currentTitle.trim()) return;
        await persistDraft(currentTitle, editor.getHTML());
    };

    // A never-saved page has no id: the first autosave creates it, which needs a title.
    const canPersist = !!pageIdState || !!title.trim();
    const autosaveEnabled = !reviewProposalId
        && canPersist
        && (!isPublished || usingDraft || draftPersisted || contentDiffers);
    const autosave = useAutosave(autosaveNow, autosaveEnabled);
    autosaveScheduleRef.current = autosave.schedule;

    // Manual "Save as Draft".
    const saveDraft = async (): Promise<string | null> => {
        if (!editor) return null;
        const currentTitle = titleRef.current?.value || title;
        if (!currentTitle.trim()) {
            toast.error("Title is required");
            return null;
        }

        autosave.cancel();
        setSavingDraft(true);
        try {
            return await persistDraft(currentTitle, editor.getHTML());
        } finally {
            setSavingDraft(false);
        }
    };

    const handlePublish = () => {
        if (!editor) return;
        const currentTitle = titleRef.current?.value || title;
        if (!currentTitle.trim()) {
            toast.error("Title is required");
            return;
        }

        autosave.cancel();
        setSaving(true);
        const body = editor.getHTML();

        const doPublish = (id: string) => {
            axios.post(`/api/manuals/pages/${id}/publish`, { title: currentTitle.trim(), body })
                .then(async res => {
                    if (res.data.success) {
                        if (reviewProposalId) {
                            // Page already updated above; resolve the request without re-applying
                            // its stored body (apply=false), then surface a combined success.
                            try {
                                await axios.post(`/api/manuals/edit-proposals/${reviewProposalId}/accept?apply=false`, { reviewNote: null });
                            } catch { /* page is saved regardless; request can be resolved manually */ }
                            toast.success("Changes applied & request accepted");
                        } else {
                            toast.success("Page published");
                        }
                        setPageId(null); // prevent lock release on unmount double-fire
                        axios.delete(`/api/manuals/pages/${id}/edit-lock`).catch(() => {});
                        onSave(id, categoryId);
                    } else {
                        toast.error(res.data.message || "Failed to publish");
                        setSaving(false);
                    }
                })
                .catch(err => {
                    toast.error(err.response?.data?.message || "Failed to publish");
                    setSaving(false);
                });
        };

        const putFolderId = (page?.folderId ?? folderId) || null;
        if (pageIdRef.current) {
            axios.put(`/api/manuals/pages/${pageIdRef.current}`, { folderId: putFolderId, title: currentTitle.trim(), body })
                .then(res => {
                    if (res.data.success) doPublish(pageIdRef.current!);
                    else { toast.error("Failed to save"); setSaving(false); }
                })
                .catch(() => { toast.error("Failed to save"); setSaving(false); });
            return;
        }
        ensureCreated(currentTitle, body).then(id => {
            if (!id) { toast.error("Failed to create"); setSaving(false); return; }
            axios.put(`/api/manuals/pages/${id}`, { folderId: putFolderId, title: currentTitle.trim(), body })
                .then(res => {
                    if (res.data.success) doPublish(id);
                    else { toast.error("Failed to save"); setSaving(false); }
                })
                .catch(() => { toast.error("Failed to save"); setSaving(false); });
        });
    };

    const handleClose = async () => {
        // Flush whatever the debounce hasn't written yet so closing never drops work.
        if (dirty && autosaveEnabled) {
            autosave.cancel();
            await autosaveNow();
        }
        if (persistedRef.current) {
            setSavedCloseConfirm(true);
            return;
        }
        if (dirty) {
            setCloseConfirm(true);
            return;
        }
        doClose();
    };

    const doClose = () => {
        if (pageIdRef.current) {
            onSave(pageIdRef.current, categoryId);
        } else {
            onClose();
        }
    };

    const handleSaveAndClose = async () => {
        setCloseConfirm(false);
        const id = await saveDraft();
        if (id) doClose();
    };

    // "Discard changes" from the close prompt just drops the in-memory edits and
    // closes; any previously-saved draft stays intact.
    const handleDiscardAndClose = () => {
        setCloseConfirm(false);
        doClose();
    };

    // Explicit "Discard draft" button. A never-saved new page just closes; an
    // existing draft is deleted; a published page's pending draft is cleared.
    const handleDiscardDraft = () => {
        if (!pageIdRef.current) {
            onClose();
            return;
        }
        setDiscardConfirm(true);
    };

    const confirmDiscardDraft = () => {
        setDiscardConfirm(false);
        setSavedCloseConfirm(false);
        autosave.cancel();
        const id = pageIdRef.current;
        if (!id) { onClose(); return; }
        axios.post(`/api/manuals/pages/${id}/discard-draft`)
            .then(res => {
                if (!res.data.success) {
                    toast.error(res.data.message || "Failed to discard draft");
                    return;
                }
                if (res.data.deleted) {
                    toast.success("Draft discarded");
                    setPageId(null); // page is gone; skip lock release
                    onClose();
                } else {
                    toast.success("Draft changes discarded");
                    onSave(id, categoryId); // return to the live page
                }
            })
            .catch(err => toast.error(err.response?.data?.message || "Failed to discard draft"));
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTitle(e.target.value);
        setDirty(true);
        evaluateDiff();
        autosave.schedule();
    };

    const handleTitleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            editor?.commands.focus("start");
        }
    };

    const addLink = () => {
        setPromptModal({
            title: "Insert link",
            placeholder: "https://example.com",
            onConfirm: (url) => {
                setPromptModal(null);
                if (editor) editor.chain().focus().setLink({ href: url }).run();
            },
        });
    };

    const addImage = () => {
        setPromptModal({
            title: "Insert image by URL",
            placeholder: "https://example.com/image.png",
            onConfirm: (url) => {
                setPromptModal(null);
                if (editor) editor.chain().focus().setImage({ src: url }).run();
            },
        });
    };

    // Upload a file to the current page's attachments endpoint and return the
    // created attachment id. Creates the page first if it doesn't exist yet
    // (the endpoint needs a page to bind to). Errors surface as toasts.
    const uploadToPage = async (
        file: File,
        kind: "inline" | "attachment",
        noun: string,
    ): Promise<string | null> => {
        const currentTitle = titleRef.current?.value || title;
        const pageId = pageIdRef.current
            || await ensureCreated(currentTitle, editor?.getHTML() || "");
        if (!pageId) {
            toast.error("Add a page title before adding files");
            return null;
        }

        const form = new FormData();
        form.append("kind", kind);
        form.append("file", file);
        const uploading = toast.loading(`Uploading ${noun}...`);
        try {
            const res = await axios.post(`/api/manuals/pages/${pageId}/attachments`, form);
            toast.dismiss(uploading);
            if (res.data.success) {
                setDirty(true);
                autosave.schedule();
                return res.data.item.id as string;
            }
            toast.error(res.data.message || "Upload failed");
            return null;
        } catch (err: any) {
            toast.dismiss(uploading);
            toast.error(err.response?.data?.message || "Upload failed");
            return null;
        }
    };

    // Inline image: validate type/size on the client, then embed by URL.
    const uploadInlineImage = async (file: File): Promise<string | null> => {
        if (!ALLOWED_INLINE_IMAGE_TYPES.includes(file.type)) {
            toast.error("Unsupported image type");
            return null;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            toast.error("Image is larger than the 25 MB limit");
            return null;
        }
        const id = await uploadToPage(file, "inline", "image");
        return id ? `/api/manuals/attachments/${id}/download` : null;
    };
    uploadImageRef.current = uploadInlineImage;

    // Non-image attachment: only the size is checked client-side; the allowed
    // file types are enforced by the backend (AttachmentsService.cs), whose
    // rejection message is surfaced by uploadToPage's error toast.
    const uploadAttachmentFile = async (
        file: File,
    ): Promise<{ url: string; filename: string } | null> => {
        if (file.size > MAX_UPLOAD_BYTES) {
            toast.error(`${file.name} is larger than the 25 MB limit`);
            return null;
        }
        const id = await uploadToPage(file, "attachment", "file");
        return id ? { url: `/api/manuals/attachments/${id}/download`, filename: file.name } : null;
    };
    uploadAttachmentRef.current = uploadAttachmentFile;

    const handleImageFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
        insertUploadedFiles(Array.from(e.target.files || []));
        e.target.value = ""; // allow re-selecting the same file
    };

    if (!editor) return null;

    return (
        <div className="m-editor-layout">
            {/* top status bar */}
            <div className="m-editor-topbar">
                <div className="m-editor-topbar-left">
                    <span className="m-editor-breadcrumb">
                        {category?.name || "Uncategorized"}
                        {folder && <> / {folder.name}</>}
                    </span>
                </div>
                <div className="m-editor-topbar-right">
                    {saveStatus === "saving" && <span className="m-editor-status">Saving...</span>}
                    {saveStatus === "saved" && <span className="m-editor-status saved">Draft saved</span>}
                    {saveStatus === "error" && <span className="m-editor-status error">Save failed</span>}
                    {!reviewProposalId && (
                        <button
                            className="m-btn m-btn-ghost"
                            onClick={handleDiscardDraft}
                            disabled={saving || savingDraft}
                        >
                            {isPublished ? "Discard changes" : "Discard draft"}
                        </button>
                    )}
                    {!reviewProposalId && (
                        <button
                            className="m-btn"
                            onClick={saveDraft}
                            disabled={saving || savingDraft}
                        >
                            {savingDraft ? "Saving..." : "Save as Draft"}
                        </button>
                    )}
                    <button
                        className="m-btn m-btn-primary"
                        onClick={handlePublish}
                        disabled={saving || savingDraft}
                    >
                        {saving
                            ? "Publishing..."
                            : reviewProposalId
                                ? "Apply & accept request"
                                : isPublished ? "Update" : "Publish"}
                    </button>
                    <button className="m-editor-close" onClick={handleClose} title="Close editor">
                        <X size={18} />
                    </button>
                </div>
            </div>

            {reviewProposalId && (
                <div className="m-editor-review-banner">
                    Reviewing a suggested edit — the body below is the requester's proposal. Adjust as needed, then
                    “Apply &amp; accept request” saves it to the page and marks the request accepted.
                </div>
            )}

            {/* toolbar */}
            <div className="m-editor-toolbar">
                <button
                    className={`m-toolbar-btn${editor.isActive("bold") ? " active" : ""}`}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    title="Bold"
                ><Bold size={16} /></button>
                <button
                    className={`m-toolbar-btn${editor.isActive("italic") ? " active" : ""}`}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    title="Italic"
                ><Italic size={16} /></button>
                <button
                    className={`m-toolbar-btn${editor.isActive("underline") ? " active" : ""}`}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    title="Underline"
                ><UnderlineIcon size={16} /></button>
                <button
                    className={`m-toolbar-btn${editor.isActive("strike") ? " active" : ""}`}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    title="Strikethrough"
                ><Strikethrough size={16} /></button>

                <ColorMenu
                    icon={<Baseline size={16} />}
                    title="Text colour"
                    swatches={TEXT_COLORS}
                    activeColor={editor.getAttributes("textStyle").color || null}
                    clearLabel="Default colour"
                    onPick={c => editor.chain().focus().setColor(c).run()}
                    onClear={() => editor.chain().focus().unsetColor().run()}
                />
                <ColorMenu
                    icon={<Highlighter size={16} />}
                    title="Highlight"
                    swatches={HIGHLIGHT_COLORS}
                    activeColor={editor.getAttributes("highlight").color || null}
                    clearLabel="No highlight"
                    onPick={c => editor.chain().focus().setHighlight({ color: c }).run()}
                    onClear={() => editor.chain().focus().unsetHighlight().run()}
                />

                <div className="m-toolbar-sep" />

                <button
                    className={`m-toolbar-btn${editor.isActive("heading", { level: 1 }) ? " active" : ""}`}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    title="Heading 1"
                ><Heading1 size={16} /></button>
                <button
                    className={`m-toolbar-btn${editor.isActive("heading", { level: 2 }) ? " active" : ""}`}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    title="Heading 2"
                ><Heading2 size={16} /></button>
                <button
                    className={`m-toolbar-btn${editor.isActive("heading", { level: 3 }) ? " active" : ""}`}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    title="Heading 3"
                ><Heading3 size={16} /></button>

                <div className="m-toolbar-sep" />

                <button
                    className={`m-toolbar-btn${editor.isActive("bulletList") ? " active" : ""}`}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    title="Bullet list"
                ><List size={16} /></button>
                <button
                    className={`m-toolbar-btn${editor.isActive("orderedList") ? " active" : ""}`}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    title="Numbered list"
                ><ListOrdered size={16} /></button>
                <button
                    className={`m-toolbar-btn${editor.isActive("blockquote") ? " active" : ""}`}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    title="Quote"
                ><Quote size={16} /></button>
                <button
                    className={`m-toolbar-btn${editor.isActive("codeBlock") ? " active" : ""}`}
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    title="Code block"
                ><Code size={16} /></button>
                <button
                    className="m-toolbar-btn"
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    title="Horizontal rule"
                ><Minus size={16} /></button>

                <div className="m-toolbar-sep" />

                <button
                    className={`m-toolbar-btn${editor.isActive("link") ? " active" : ""}`}
                    onClick={addLink}
                    title="Insert link"
                ><Link size={16} /></button>
                <button
                    className="m-toolbar-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title="Upload image"
                ><ImagePlus size={16} /></button>
                <button
                    className="m-toolbar-btn"
                    onClick={addImage}
                    title="Insert image by URL"
                ><Image size={16} /></button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleImageFilePicked}
                />

                <div className="m-toolbar-sep" />

                <button
                    className="m-toolbar-btn"
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                    title="Undo"
                ><Undo size={16} /></button>
                <button
                    className="m-toolbar-btn"
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                    title="Redo"
                ><Redo size={16} /></button>
            </div>

            {/* editor area */}
            <div className="m-editor-body">
                <div className="m-editor-content">
                    <input
                        ref={titleRef}
                        className="m-editor-title"
                        value={title}
                        onChange={handleTitleChange}
                        onKeyDown={handleTitleKeyDown}
                        placeholder="Page Title"
                        maxLength={512}
                    />
                    <EditorContent editor={editor} className="m-tiptap" />
                </div>
            </div>

            {promptModal && (
                <PromptModal
                    title={promptModal.title}
                    placeholder={promptModal.placeholder}
                    onConfirm={promptModal.onConfirm}
                    onCancel={() => setPromptModal(null)}
                />
            )}

            {closeConfirm && (
                <ConfirmModal
                    title="Unsaved changes"
                    message="You have unsaved changes. Save them as a draft before closing?"
                    confirmLabel="Save as draft"
                    cancelLabel="Discard changes"
                    onConfirm={handleSaveAndClose}
                    onCancel={handleDiscardAndClose}
                />
            )}

            {savedCloseConfirm && (
                <ConfirmModal
                    title="Changes saved as draft"
                    message={isPublished
                        ? "Your changes are saved as an unpublished draft and the published page is unchanged. Discard the draft instead?"
                        : "Your changes are saved as a draft. Discard it instead? The page has never been published, so it will be deleted."}
                    confirmLabel="Discard draft"
                    cancelLabel="Keep draft"
                    danger
                    onConfirm={confirmDiscardDraft}
                    onCancel={() => { setSavedCloseConfirm(false); doClose(); }}
                />
            )}

            {discardConfirm && (
                <ConfirmModal
                    title={isPublished ? "Discard draft changes" : "Discard draft"}
                    message={isPublished
                        ? "Discard your unpublished changes? The published page stays exactly as it is now."
                        : "Discard this draft? The page has never been published, so it will be deleted."}
                    confirmLabel="Discard"
                    danger
                    onConfirm={confirmDiscardDraft}
                    onCancel={() => setDiscardConfirm(false)}
                />
            )}
        </div>
    );
}
